import { useCallback, useRef } from 'react';

import { type SearchPreloadDataProvider } from '../../../common/types/search-preload';
import { recordInteractionOffset } from '../../../common/utils/perf/query-layer-timings';

const PRELOAD_NAVIGATE_AT = 'preloadNavigateAt';
const PRELOAD_START_AT = 'preloadStartAt';

export type ReportPreloadTimings = ({ cloudId }: { cloudId?: string }) => void;

/**
 * Returns a reporter that, on the first retrieval query of the page, records
 * the preload marks (`preloadNavigateAt`/`preloadStartAt`) as UFO custom data
 * — offsets from the active interaction's start, mirroring
 * `recordQueryDispatch`/`recordQueryDataAvailable` in `query-layer-timings.ts`.
 * Durations are derived downstream from these raw marks rather than
 * precomputed here.
 *
 * Deliberately does not record its own dispatch mark: the one-shot latch
 * below means these marks are only ever written on the interaction the
 * original, preload-anchored navigation landed on — the same interaction
 * `recordQueryDispatch` writes `asyncQueryDispatchAt` for at the call site in
 * `query-plan/index.ts` (same `dispatchAt`, same interaction). A re-search
 * never revisits that interaction — it starts a brand new one via
 * `startReSearchTransition`/`traceUFOTransition` — so `asyncQueryDispatchAt`
 * on the interaction that carries these marks is never overwritten by a later,
 * unrelated dispatch. Downstream analysis can pair `preloadStartAt` with the
 * existing `asyncQueryDispatchAt` directly.
 *
 * Latches after the first preload it measures and no-ops when there is no
 * preload, mirroring `usePreloadQueryVarsDiffReporter`. Must be called before
 * the preload data provider is cleared.
 */
export function usePreloadTimingsReporter(
	preloadDataProvider?: SearchPreloadDataProvider,
): ReportPreloadTimings {
	const hasReportedRef = useRef(false);

	return useCallback<ReportPreloadTimings>(
		({ cloudId }) => {
			if (hasReportedRef.current) {
				return;
			}

			if (!cloudId || !preloadDataProvider) {
				return;
			}

			const timings = preloadDataProvider.getPreloadedSearch({ cloudId })?.search?.timings;

			// No preload for this navigation (or timings were not captured) — nothing
			// to measure. Deliberately not latched, so a later navigation that does
			// preload can still be reported.
			if (!timings) {
				return;
			}

			hasReportedRef.current = true;

			if (timings.navigateAt !== undefined) {
				recordInteractionOffset(PRELOAD_NAVIGATE_AT, timings.navigateAt);
			}
			if (timings.preloadStartAt !== undefined) {
				recordInteractionOffset(PRELOAD_START_AT, timings.preloadStartAt);
			}
		},
		[preloadDataProvider],
	);
}
